import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import s from "./styles.scss";
import {
  faEdit,
  faUserEdit,
  faFileExport,
  faUndo,
  faListAlt,
  faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";
import MembresiaSnapshot from "../../../../types/MembresiaSnapshot";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";
import MostrarCambiosModal from "./MostrarCambiosModal";

export interface TableProps {
  page: Page<MembresiaSnapshot>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onActualizarSnapshot: (row: MembresiaSnapshot) => void;
  onCambiarVendedor: (row: MembresiaSnapshot) => void;
  onDescartarCambios: (row: MembresiaSnapshot) => void;
}

export const MemoTable = React.memo(
  ({
    page,
    loading,
    onFetchData,
    onExport,
    onActualizarSnapshot,
    onCambiarVendedor,
    onDescartarCambios
  }: TableProps) => {
    const [modalCambiosOpen, setModalCambiosOpen] = useState(false);
    const [
      snapshotSeleccionado,
      setSnapshotSeleccionado
    ] = useState<MembresiaSnapshot | null>(null);
    const [columns, setColumns] = useState(
      columnsFormatter(
        onActualizarSnapshot,
        onCambiarVendedor,
        onDescartarCambios,
        (row: MembresiaSnapshot) => {
          setSnapshotSeleccionado(row);
          setModalCambiosOpen(true);
        }
      )
    );

    useEffect(() => {
      setColumns(
        columnsFormatter(
          onActualizarSnapshot,
          onCambiarVendedor,
          onDescartarCambios,
          (row: MembresiaSnapshot) => {
            setSnapshotSeleccionado(row);
            setModalCambiosOpen(true);
          }
        )
      );
    }, [onActualizarSnapshot, onCambiarVendedor, onDescartarCambios]);

    return (
      <div>
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span>
                  &nbsp;&nbsp;&nbsp;&nbsp;Membresías mostradas:{" "}
                  {page.numberOfElements} de {page.totalElements}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  id="btnReportExportCsv"
                  color="black"
                  type="button"
                  onClick={onExport}
                >
                  Exportar <FontAwesomeIcon icon={faFileExport} color="black" />
                </Button>
              </div>
            </>
          )}
        </div>
        <ReactTable
          manual
          data={page ? page.content : []}
          columns={columns}
          loading={loading}
          onFetchData={onFetchData}
          pageSize={page && page.content.length > 5 ? page.content.length : 5}
          showPagination={false}
          className="-striped -highlight"
          loadingText="Cargando..."
          noDataText="No hay registros"
        />
        {snapshotSeleccionado && (
          <MostrarCambiosModal
            isOpen={modalCambiosOpen}
            snapshot={snapshotSeleccionado}
            onClose={() => {
              setModalCambiosOpen(false);
              setSnapshotSeleccionado(null);
            }}
          />
        )}
      </div>
    );
  }
);

function columnsFormatter(
  onActualizarSnapshot: (row: MembresiaSnapshot) => void,
  onCambiarVendedor: (row: MembresiaSnapshot) => void,
  onDescartarCambios: (row: MembresiaSnapshot) => void,
  onMostrarCambios: (row: MembresiaSnapshot) => void
) {
  return [
    {
      Header: "Cotización",
      id: "idCotizacionItem",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 80,
      accessor: simpleAccessor("idCotizacionItem")
    },
    {
      Header: "Periodo",
      id: "periodo",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: dateAccessor("periodo")
    },
    {
      Header: "Factura",
      id: "folioFactura",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 96,
      accessor: simpleAccessor("folioFactura")
    },
    {
      Header: "Vendedor",
      id: "vendedor",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("vendedor")
    },
    {
      Header: "Operación",
      id: "tipoOperacion",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 104,
      accessor: row => {
        switch (row.tipoOperacion) {
          case "VENTA_MEMBRESIA":
            return <div className={fieldClassName(row)}>Venta</div>;
          case "REACTIVACION":
            return <div className={fieldClassName(row)}>Reactivación</div>;
          case "INTEGRANTE_INDIVIDUAL":
            return <div className={fieldClassName(row)}>Integrante</div>;
          default:
            return <div className={fieldClassName(row)}>No reconocida</div>;
        }
      }
    },
    {
      Header: "Membresía",
      id: "membresiaLink",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 150,
      accessor: (row: MembresiaSnapshot) => {
        const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
        const label = `${row.idPublico} de ${row.clubBase}`;

        return (
          <div className={fieldClassName(row)}>
            <a href={href} target="_blank" rel="noreferrer">
              {label}
            </a>
          </div>
        );
      }
    },
    {
      Header: "Tipo",
      id: "tipoMembresia",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 104,
      accessor: (row: MembresiaSnapshot) => {
        return (
          <div className={fieldClassName(row)}>
            {(row.membresia || "NA").replace("Membresía ", "")}&nbsp;
          </div>
        );
      }
    },
    {
      Header: "Precio Venta",
      id: "precioVenta",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: moneyAccessor("precioVenta")
    },
    {
      Header: "Alcance",
      id: "cuentaAlcance",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: (row: MembresiaSnapshot) => {
        const btnId = `venta-asesor-${row.idCotizacionItem}`;

        // Si cuenta para alcance pero tiene 0 puntos, mostrar razón en amarillo
        if (row.cuentaAlcance && row.puntosAlcance === 0) {
          return (
            <div className={fieldClassName(row)}>
              {renderRazones(
                false,
                ["El Plan Parcial Vespertino no añade puntos de alcance"],
                `${btnId}-cuentaAlcance`,
                "#ac992d"
              )}
            </div>
          );
        }

        return (
          <div className={fieldClassName(row)}>
            {row.cuentaAlcance
              ? "Sí"
              : renderRazones(
                  row.cuentaAlcance,
                  row.cuentaAlcanceRazones,
                  `${btnId}-cuentaAlcance`
                )}
          </div>
        );
      }
    },
    {
      Header: "Comisionable",
      id: "comisionable",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: (row: MembresiaSnapshot) => {
        const btnId = `venta-asesor-${row.idCotizacionItem}`;

        return (
          <div className={fieldClassName(row)}>
            {row.cuentaComision
              ? "Sí"
              : renderRazones(
                  row.cuentaComision,
                  row.cuentaComisionRazones,
                  `${btnId}-cuentaComision`
                )}
          </div>
        );
      }
    },
    {
      Header: "Cump. Reglas",
      id: "cumplReglas",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: (row: MembresiaSnapshot) => {
        const btnId = `venta-asesor-${row.idCotizacionItem}`;

        return (
          <div className={fieldClassName(row)}>
            {row.cuentaReglas
              ? "Sí"
              : renderRazones(
                  row.cuentaReglas,
                  row.cuentaReglasRazones,
                  `${btnId}-cuentaReglas`,
                  !row.cuentaAlcance || !row.cuentaComision
                    ? "#ffffff"
                    : "#8b0000"
                )}
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function booleanAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    return (
      <div className={fieldClassName(row)}>
        {!!row[key] ? "Sí" : "No"}&nbsp;
      </div>
    );
  };
}

function dateAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function moneyAccessor(accessor: string) {
  return row => {
    const cantidad = Number(row[accessor]);

    return (
      <div className={fieldClassName(row) + " text-right"}>
        {!Number.isNaN(cantidad) && row[accessor] != null
          ? formatMoney(cantidad, 2)
          : "-"}
      </div>
    );
  };
}

function renderRazones(
  cumplimiento: boolean,
  razones: string[],
  id: string,
  color?: string
) {
  if (cumplimiento || razones == null) {
    return null;
  }

  const tooltip =
    razones.length > 0
      ? razones.map((razon, idx) => <div key={idx}>• {razon}</div>)
      : "No cumple con los requisitos de comisionabilidad";

  return (
    <span className={s.info} id={id}>
      <FontAwesomeIcon icon={faExclamationCircle} color={color || "#ffffff"} />
      <UncontrolledTooltip placement="left" target={id}>
        <div style={{ textAlign: "left" }}>
          <strong>Incumplimiento:</strong>
          {tooltip}
        </div>
      </UncontrolledTooltip>
    </span>
  );
}

function renderBoton(
  row: MembresiaSnapshot,
  id,
  habilitado,
  action,
  tooltip,
  color,
  icon
) {
  if (habilitado) {
    return (
      <span id={id} onClick={() => action(row)} className={s.btn}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  } else {
    return (
      <span className={s.info} id={id}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  }
}

function fieldClassName(row: MembresiaSnapshot) {
  let className = s.field;

  if (!row.cuentaAlcance || !row.cuentaComision) {
    className += ` ${s.incumplimiento}`;
    return className;
  }

  if (!row.cuentaReglas) {
    className += ` ${s.facturaCancelada}`;
  }

  if (row.tieneCambios) {
    className += ` ${s.conCambios}`;
  } else if (row.evaluadorModifico) {
    className += ` ${s.conActualizaciones}`;
  }

  return className;
}
